import { NavLink } from "react-router-dom";

import { AppendixIntro } from "../components/AppendixIntro";
import { DataTableCard } from "../components/DataTableCard";
import { MetricCard } from "../components/MetricCard";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";

type SourcesPageData = Awaited<ReturnType<typeof api.getSources>>;

function CompactList({
  eyebrow,
  title,
  items,
  tone
}: {
  eyebrow: string;
  title: string;
  items: string[];
  tone: "neutral" | "warning";
}) {
  const accent = tone === "warning" ? "#ffcf7d" : "#7fd0ff";

  return (
    <section
      style={{
        borderRadius: "22px",
        padding: "20px",
        background: "linear-gradient(180deg, rgba(9, 14, 26, 0.88), rgba(7, 11, 21, 0.78))",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 16px 34px rgba(3, 8, 20, 0.18)"
      }}
    >
      <div className="stack-sm">
        <div className="stack-xs">
          <span className="card-eyebrow">{eyebrow}</span>
          <h3 className="card-title">{title}</h3>
        </div>
        <div style={{ display: "grid", gap: "12px" }}>
          {items.map((item, index) => (
            <div
              key={`${item}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: "26px minmax(0, 1fr)",
                gap: "10px",
                alignItems: "start",
                paddingTop: index === 0 ? 0 : "12px",
                borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.06)"
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "26px",
                  height: "26px",
                  borderRadius: "999px",
                  background: `${accent}22`,
                  border: `1px solid ${accent}55`,
                  color: accent,
                  fontSize: "0.76rem",
                  fontWeight: 700
                }}
              >
                {index + 1}
              </span>
              <div style={{ color: "rgba(221, 231, 247, 0.8)", lineHeight: 1.5 }}>{item}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SourcesContent({ data }: { data: SourcesPageData }) {
  const evidenceRows = [
    ["Evidence standard", "Curated public trust layer", "Explains basis without turning the site into a data room"],
    ["Evidence basis", data.summary, "High-level public evidence posture only"],
    ["Coverage footprint", `${data.sectionCoverage.length} covered areas`, "Supports published surfaces without acting as an archive"],
    ["Reliability posture", `${data.reliabilityBreakdown.length} public reliability buckets`, "Keeps confidence visible without over-claiming precision"]
  ];

  const representativeRows = data.representativeSources.map((item) => [
    item.name,
    `${item.type} | ${item.reliability}`,
    item.reference,
    item.sectionsSupported.join(", ")
  ]);

  const categoryRows = data.sourceCategoryCounts.map((item) => [
    item.category,
    `${item.count}`,
    data.reliabilityBreakdown[0]?.label ?? "Public summary coverage"
  ]);

  const methodologyRows = [
    ["Methodology scope", "Curated published-basis summary", "Explains what supports the live site, not every internal research branch"],
    ["Verified vs modeled", "Framework disclosed, not fully claim-tagged", "Runtime does not yet expose a full claim-level evidence ledger"],
    ["Currentness", "Controlled but not fully contracted in runtime", "No full public source-by-source freshness ledger is exposed here"],
    ["Public disclosures", `${data.limitations.length} disclosed limitations`, "Limits are shown directly instead of being implied away"]
  ];

  return (
    <div className="stack-xl">
      <AppendixIntro
        eyebrow="Appendix / Trust Layer"
        title="Evidence standard behind the site."
        body="This page now behaves like an appendix-grade trust layer. It explains the evidence basis, representative sources, and public limitations compactly, without sounding like a process memo or competing with the core investor pages."
        noteTitle="How to use this appendix"
        note="Use this page to calibrate trust after the main investor read is already clear. Scan the evidence standard, representative sources, and stated limitations, then return to the core decision surfaces."
        actions={
          <>
            <NavLink to="/investment-case" className="link-button link-button-secondary">
              Investment Case
            </NavLink>
            <NavLink to="/market-support" className="link-button link-button-secondary">
              Market Support
            </NavLink>
          </>
        }
      />

      <div
        style={{
          display: "grid",
          gap: "16px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))"
        }}
      >
        <MetricCard title="Representative sources" value={`${data.representativeSources.length}`} detail="Named public examples, not a raw source-log dump" />
        <MetricCard title="Source categories" value={`${data.sourceCategoryCounts.length}`} detail="Current evidence groupings visible in public" />
        <MetricCard title="Coverage areas" value={`${data.sectionCoverage.length}`} detail="Published diligence areas currently supported" />
        <MetricCard title="Disclosed limits" value={`${data.limitations.length}`} detail="Known trust-layer boundaries remain explicit" />
      </div>

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
        }}
      >
        <DataTableCard
          eyebrow="Evidence Standard"
          title="Trust-layer summary"
          description="The evidence standard now sits above the fold in one concise table instead of being spread across methodology prose."
          columns={["Layer", "Current read", "Meaning"]}
          rows={evidenceRows}
        />
        <DataTableCard
          eyebrow="Representative Sources"
          title="Named public examples"
          description="Examples are shown to explain basis and coverage, not to recreate an internal source browser."
          columns={["Source", "Type / reliability", "Reference", "Supported areas"]}
          rows={representativeRows}
        />
      </div>

      <section
        style={{
          borderRadius: "24px",
          padding: "22px",
          background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
        }}
      >
        <div className="stack-md">
          <div className="stack-xs">
            <span className="card-eyebrow">Lower-Priority Methodology Detail</span>
            <h3 className="card-title">Methodology and coverage stay available, but they now sit beneath the trust summary.</h3>
            <p className="card-copy">
              This lower section preserves the public methodology boundary and supporting coverage detail without letting it dominate attention above the fold.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gap: "18px",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
            }}
          >
            <DataTableCard
              eyebrow="Methodology Scope"
              title="What the runtime does and does not expose"
              description="The public methodology layer is explicit about scope and current gaps instead of pretending runtime precision that does not exist."
              columns={["Area", "Current read", "Implication"]}
              rows={methodologyRows}
            />

            <DataTableCard
              eyebrow="Coverage Summary"
              title="Source category view"
              description="Category counts keep the evidence layer readable without inflating it into a full inventory browser."
              columns={["Category", "Count", "Current read"]}
              rows={categoryRows}
            />
          </div>

          <div
            style={{
              display: "grid",
              gap: "18px",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))"
            }}
          >
            <CompactList
              eyebrow="Current Limitations"
              title="What the public layer is not claiming"
              items={data.limitations}
              tone="warning"
            />
            <CompactList
              eyebrow="Internal-Only Exclusions"
              title="What stays out of public view"
              items={[
                "Raw source-log batches remain internal and are not reproduced here.",
                "Internal process notes and workflow detail remain excluded from the public site.",
                "The full internal evidence inventory is not exposed as a browsing interface.",
                "Where runtime lacks claim-level tagging or a full freshness ledger, the gap is disclosed instead of being backfilled with invented precision."
              ]}
              tone="neutral"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

export function SourcesPage() {
  const { data, loading, error } = useContent("sources-methodology-page", api.getSources);

  return (
    <PageContainer
      eyebrow="Appendix / Sources"
      title="Evidence basis and public trust summary."
      lede="This appendix screen keeps representative sources, methodology scope, and stated limitations visible without letting the trust layer compete with the primary investor pages."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading sources and methodology...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? <SourcesContent data={data} /> : null}
    </PageContainer>
  );
}
